/**
 * 0.82: Summen, Einheiten und berechnete Felder (Paket D).
 * - Auswertung je Feld (`config.aggregate`): Summe, Durchschnitt, Minimum, Maximum, Anzahl. Ohne Einstellung
 *   erscheint nirgends etwas; ohne Werte ebenfalls nicht.
 * - Einheiten (`config.unit`, `config.decimals`) für Zahlen, Währung (`config.currency`) für Beträge –
 *   verschiedene Einheiten/Währungen werden nie zusammengezählt.
 * - Berechnete Felder (`type: 'formula'`, `config.formula`): Formel mit Feldnamen in eckigen Klammern,
 *   exakt dezimal (wie Formulare). Der Server speichert das Ergebnis in der Karte (Filter, Export, Summen).
 */
import { evalFormulaNumber, exprRefs, formatExpr, FormulaError, parseFormula, roundDecimal, type FormExpr } from './forms.js';
import type { CustomField, FieldType } from './types.js';

export type AggregateKind = 'sum' | 'avg' | 'min' | 'max' | 'count';
export const AGGREGATE_LABEL: Record<AggregateKind, string> = { sum: 'Summe', avg: 'Durchschnitt', min: 'Minimum', max: 'Maximum', count: 'Anzahl' };
const AGG_SIGN: Record<AggregateKind, string> = { sum: 'Σ', avg: 'Ø', min: 'min', max: 'max', count: '' };

/** 0.85: Rechengenauigkeit berechneter Zahlen (Stellen), wenn nicht ausdrücklich gerundet wird */
export const FORMULA_PRECISION = 10;

export interface FormulaConfig {
  /** Formel mit Feld-IDs als Verweisen */
  expr: FormExpr;
  /** Ergebnis als Zahl oder Betrag */
  result: 'number' | 'currency';
  /**
   * 0.85 (N04): bewusst vor dem Weiterrechnen auf so viele Stellen runden (nur Zahl; Beträge immer auf den
   * Cent). Ohne Angabe rechnet KaBa genau weiter – `decimals` ist dann nur die Anzeige.
   */
  round?: number | null;
}

/** Zahlenartige Felder (rechnen, summieren) */
export const isNumeric = (t: FieldType) => t === 'number' || t === 'currency' || t === 'formula' || t === 'rating';

/** Welche Auswertungen zu einem Feldtyp passen (leer = keine) */
export function aggregateOptions(t: FieldType): AggregateKind[] {
  if (t === 'number' || t === 'currency' || t === 'formula') return ['sum', 'avg', 'min', 'max', 'count'];
  if (t === 'rating') return ['avg', 'min', 'max', 'count'];
  if (t === 'checkbox' || t === 'text' || t === 'select' || t === 'multiselect' || t === 'person' || t === 'date' || t === 'url' || t === 'email' || t === 'phone') return ['count'];
  return [];
}

type FieldLike = Pick<CustomField, 'id' | 'name' | 'type' | 'options'> & { config?: CustomField['config'] };

const filled = (v: unknown) => v !== null && v !== undefined && v !== '' && v !== false && !(Array.isArray(v) && v.length === 0);

export interface AggregateResult {
  kind: AggregateKind;
  /** Ergebnis; null = nichts zu zeigen */
  value: number | null;
  /** Anzahl berücksichtigter Werte */
  n: number;
}

/** Werte eines Feldes auswerten (Karten-Werte in beliebiger Reihenfolge) */
export function aggregate(field: FieldLike, values: unknown[], kind = field.config?.aggregate ?? null): AggregateResult | null {
  if (!kind || !aggregateOptions(field.type).includes(kind)) return null;
  if (kind === 'count') {
    const n = values.filter(filled).length;
    return { kind, value: n, n };
  }
  const nums = values.map((v) => (typeof v === 'number' ? v : v === null || v === undefined || v === '' ? null : Number(v))).filter((v): v is number => v !== null && Number.isFinite(v));
  if (!nums.length) return { kind, value: null, n: 0 };
  // Exakt dezimal addieren (keine 0,1 + 0,2-Artefakte bei Beträgen)
  const dec = field.type === 'currency' || (field.type === 'formula' && field.config?.formula?.result === 'currency') ? 2 : 6;
  const sum = nums.reduce((a, b) => roundDecimal(a + b, 10), 0);
  const value =
    kind === 'sum' ? roundDecimal(sum, dec) : kind === 'avg' ? roundDecimal(sum / nums.length, dec) : kind === 'min' ? Math.min(...nums) : Math.max(...nums);
  return { kind, value, n: nums.length };
}

/** Einheit für die Anzeige: „€“, „CHF“, „Std.“ … */
export function fieldUnit(f: FieldLike): string | null {
  const c = f.config;
  if (f.type === 'currency' || (f.type === 'formula' && c?.formula?.result === 'currency')) return c?.currency || 'EUR';
  return c?.unit?.trim() || null;
}

/** Zahl eines Feldes formatieren (Währung, Einheit, Nachkommastellen) */
export function formatNumberOf(f: FieldLike, n: number): string {
  const c = f.config;
  const money = f.type === 'currency' || (f.type === 'formula' && c?.formula?.result === 'currency');
  if (money) {
    try {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: c?.currency || 'EUR' }).format(n);
    } catch {
      return `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c?.currency ?? ''}`.trim();
    }
  }
  const d = c?.decimals;
  const text = n.toLocaleString('de-DE', d === null || d === undefined ? { maximumFractionDigits: 10 } : { minimumFractionDigits: d, maximumFractionDigits: d });
  const unit = c?.unit?.trim();
  return unit ? `${text} ${unit}` : text;
}

/** „Σ 4.230,00 €“, „Ø 3,5 Std.“, „12 angehakt“ – null, wenn es nichts zu zeigen gibt */
export function formatAggregate(f: FieldLike, r: AggregateResult | null): string | null {
  if (!r || r.value === null) return null;
  if (r.kind === 'count') return r.value ? `${r.value} ${f.type === 'checkbox' ? 'angehakt' : 'ausgefüllt'}` : null;
  return `${AGG_SIGN[r.kind]} ${formatNumberOf(f, r.value)}`;
}

/** Können zwei Felder zusammen ausgewertet werden (gleiche Art, gleiche Einheit/Währung)? */
export function sameMeasure(a: FieldLike, b: FieldLike): boolean {
  const kind = (f: FieldLike) => (f.type === 'currency' || (f.type === 'formula' && f.config?.formula?.result === 'currency') ? 'money' : isNumeric(f.type) ? 'number' : f.type);
  return kind(a) === kind(b) && (fieldUnit(a) ?? '') === (fieldUnit(b) ?? '');
}

/* ---------- Berechnete Felder ---------- */

/** Formel aus dem Editor lesen: Verweise als [Feldname] auf Zahl-, Betrags-, Bewertungs- oder berechnete Felder */
export function parseFieldFormula(src: string, fields: FieldLike[], selfId: string | null): FormExpr {
  return parseFormula(src, (label) => {
    const f = fields.find((x) => x.name.trim().toLowerCase() === label.toLowerCase() && x.id !== selfId);
    if (!f) return null;
    if (!isNumeric(f.type)) throw new FormulaError(`„${f.name}“ ist kein Zahlenfeld.`);
    return f.id;
  });
}

export function formatFieldFormula(e: FormExpr | undefined, fields: FieldLike[]): string {
  return formatExpr(e, (id) => fields.find((f) => f.id === id)?.name ?? '?').replaceAll('×', '*').replaceAll('÷', '/');
}

export function formulaRefs(e: FormExpr): string[] {
  return exprRefs(e).refs;
}

/**
 * Alle berechneten Felder einer Karte (Abhängigkeiten zuerst, Kreise → leer). Gibt nur die berechneten
 * Felder zurück (null = leer, z. B. weil ein Wert fehlt).
 */
export function computeFormulas(fields: FieldLike[], custom: Record<string, unknown>): Record<string, number | null> {
  const formulas = fields.filter((f) => f.type === 'formula' && f.config?.formula);
  const out: Record<string, number | null> = {};
  const state = new Map<string, 'busy' | 'done'>();
  const byId = new Map(fields.map((f) => [f.id, f]));
  const valueOf = (id: string): number | null => {
    const f = byId.get(id);
    if (!f) return null;
    if (f.type !== 'formula') {
      const v = custom[id];
      return typeof v === 'number' && Number.isFinite(v) ? v : null;
    }
    if (state.get(id) === 'done') return out[id] ?? null;
    if (state.get(id) === 'busy') return null; // Kreis
    state.set(id, 'busy');
    const cfg = f.config!.formula!;
    let v = evalFormulaNumber(cfg.expr, valueOf);
    // 0.85 (N04): Nachkommastellen sind nur Anzeige; gespeichert wird genau (10 Stellen), gerundet nur auf Wunsch bzw. Beträge auf den Cent
    if (v !== null) v = roundDecimal(v, cfg.result === 'currency' ? 2 : (cfg.round ?? FORMULA_PRECISION));
    out[id] = v !== null && Number.isFinite(v) ? v : null;
    state.set(id, 'done');
    return out[id]!;
  };
  for (const f of formulas) valueOf(f.id);
  return out;
}

/**
 * 0.86: Warum ist ein berechnetes Feld leer? Fehlende Eingangswerte (Feldnamen) oder ein Rechenfehler
 * (Division durch 0, Kreis). null = es gibt einen Wert (oder das Feld ist keine Formel).
 */
export function formulaGap(fields: FieldLike[], custom: Record<string, unknown>, fieldId: string): { missing: string[] } | { error: 'div0' | 'cycle' } | null {
  const f = fields.find((x) => x.id === fieldId);
  if (!f || f.type !== 'formula' || !f.config?.formula) return null;
  const values = computeFormulas(fields, custom);
  if (values[fieldId] !== null && values[fieldId] !== undefined) return null;
  if (formulaCycle(fields, fieldId, f.config.formula.expr)) return { error: 'cycle' };
  const byId = new Map(fields.map((x) => [x.id, x]));
  const missing = [...new Set(formulaRefs(f.config.formula.expr))]
    .filter((id) => {
      const g = byId.get(id);
      if (!g) return true;
      const v = g.type === 'formula' ? values[id] : custom[id];
      return !(typeof v === 'number' && Number.isFinite(v));
    })
    .map((id) => byId.get(id)?.name ?? 'gelöschtes Feld');
  return missing.length ? { missing } : { error: 'div0' };
}

/** Kurztext für `formulaGap` („fehlt: Ist“, „Division durch 0“) */
export function formulaGapText(g: ReturnType<typeof formulaGap>): string | null {
  if (!g) return null;
  if ('missing' in g) return `Noch leer – es fehlt ${g.missing.map((n) => `„${n}“`).join(', ')}`;
  return g.error === 'cycle' ? 'Rechenfehler: Die Formel verweist auf sich selbst' : 'Rechenfehler: Division durch 0';
}

/** Hängt eine Formel (direkt oder über andere Formeln) von sich selbst ab? */
export function formulaCycle(fields: FieldLike[], selfId: string, expr: FormExpr): boolean {
  const byId = new Map(fields.map((f) => [f.id, f]));
  const seen = new Set<string>();
  const walk = (refs: string[]): boolean =>
    refs.some((r) => {
      if (r === selfId) return true;
      if (seen.has(r)) return false;
      seen.add(r);
      const f = byId.get(r);
      return f?.type === 'formula' && f.config?.formula ? walk(formulaRefs(f.config.formula.expr)) : false;
    });
  return walk(formulaRefs(expr));
}

export const CURRENCIES = ['EUR', 'CHF', 'USD', 'GBP', 'PLN', 'CZK', 'DKK', 'SEK', 'NOK'] as const;
